import React, { useEffect, useState } from 'react';
import { Image, Page, pdf, PDFDownloadLink, PDFViewer, StyleSheet, Text, View } from '@react-pdf/renderer';
import PdfGenerator from '../../components/Dossier/PdfGenerator';
import useAuthz from '../../hooks/useAuthz';
import { useNavigate } from 'react-router';
import axios from 'axios';
import { URL_API } from '../../utils/globalConfig';
import MyButton from '../../components/Layout/MyButton';
import { faArrowLeft, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { faSave } from '@fortawesome/free-regular-svg-icons';
import TopBar from '../../components/Layout/TopBar';
import logoSanef from '../../images/logo.png';
import { useAlert } from 'react-alert';
import moment from 'moment';
import { REQUEST_PASS_TYPE } from '../../constants/enum';
import htmlToDraft from 'html-to-draftjs';

const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        fontSize: '12px',
        height: 'auto',
        paddingBottom: '45px',
    },

    row: {
        flexDirection: 'row',
    },
    caracRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        lineHeight: '1.2',
        minHeight: '10px',
    },
    sectionHead: {
        margin: 10,
        padding: 10,
        maxHeight: '20%',
    },
    sectionBody: {
        margin: 10,
        padding: 10,
        height: 'auto',
    },
    sectionFoot: {
        margin: 10,
        padding: 10,
        flexGrow: 1,
        maxHeight: '80px',
    },
    pdfBody: {
        fontFamily: 'Courier',
        fontSize: '11px',
    },
    bold: {
        fontWeight: 'bold',
    },
    underline: {
        textDecoration: 'underline',
    },
    italic: {
        fontStyle: 'italic',
    },
});

// TODO: stocker les informations dans le local storage et les supprimées quand on sauvegarde
const ContractViewer = (props) => {
    const requestPassId = new URLSearchParams(location.search).get('id') || 0;
    const planificationId = new URLSearchParams(location.search).get('planificationId') || 0;
    const navigate = useNavigate();
    const alert = useAlert();
    const currentDate = moment();
    const [authz, user, profil] = useAuthz();
    const [demandePassage, setDemandePassage] = useState(null);
    const [planification, setPlanification] = useState(null);
    const [saveContract, setSaveContract] = useState({
        id: requestPassId,
        data: null,
        fileName: 'Accords de passage demande n° [' + requestPassId + ']',
        mimeType: 'application/pdf',
        isInit: true,
    });
    const [signature, setSignature] = useState(null);
    const [fileName, setFileName] = useState('');
    const [legalNotice, setLegalNotice] = useState('');
    const [particularNotice, setParticularNotice] = useState('');
    const [pdfBlob, setPdfBlob] = useState(null);
    const [numDemand, setNumDemand] = useState('');

    //#region useEffect

    useEffect(async () => {
        if (profil.profil === 1 || profil.profil === 2) {
            // Chargement de la demande de passage

            await Promise.all([
                apiGetSignature(),
                await apiGetLegalNotice(),
                await apiGetParticularNotice(),
            ]).then(() => apiGetRequestPass());

        } else {
            alert.error('Vous n\'êtes pas autorisé à accéder à la création d\'un dossier');
            navigate('/');
        }
    }, []);

    //#endregion

    // region API
    const apiGetRequestPass = () => {
        return axios({
            method: 'GET', url: `${URL_API}/demande-passage/recuperer-demande`, params: {
                'id': requestPassId,
            },
        })
        .then((response) => {
            alert.removeAll();
            let requestPassData = response.data;
            if (requestPassData) {
                setDemandePassage(requestPassData);
                if (requestPassData.planifications) {
                    let planif = requestPassData.planifications.find((planif) => Number(planif.id) === Number(planificationId));
                    if (planif) {
                        setPlanification(planif);
                    }

                    let currentPlanification = requestPassData.planifications.find((planification2) => {
                        return Number(planification2.id) === Number(planificationId);
                    });

                    let currentPlanifIndex = requestPassData.planifications
                    .filter((aPlanification) => aPlanification.deleted === false)
                    .sort((a, b) => a.id - b.id)
                    .findIndex((aPlanification) => Number(aPlanification.id) === Number(planificationId)) + 1;

                    let nbPlanification = 0;

                    if (currentPlanification) {
                        nbPlanification = requestPassData.metadata.datePassage.nbPassageAller + requestPassData.metadata.datePassage.nbPassageRetour;
                    }

                    setNumDemand(`${currentPlanifIndex}/${nbPlanification ?? 0}`);

                      let departmentNumbers = requestPassData.metadata.reference.departments.map(department => {
                        const match = department.match(/\((\d+)\)/);
                        return match ? match[1] : null;
                    }).filter(Boolean);
                    setFileName(`[ACCORDS-PASSAGE]_[${requestPassId}-${currentPlanifIndex}-${requestPassData.metadata.reference.numDossier}-${departmentNumbers.join('-')}]_[${moment(requestPassData.metadata.datePassage.startDate).format('DD-MM-YYYY')}]`);
                    setSaveContract({ ...saveContract, fileName: `[ACCORDS-PASSAGE]_[${requestPassId}-${currentPlanifIndex}-${requestPassData.metadata.reference.numDossier}-${departmentNumbers.join('-')}]_[${moment(requestPassData.metadata.datePassage.startDate).format('DD-MM-YYYY')}]` });
                }
            }
        })
        .catch((error) => {
            alert.removeAll();
            alert.error(error.message);
        });
    };

    const apiGetSignature = () => {
        if (requestPassId > 0) {
            axios({ method: 'get', url: URL_API + '/parameters/get-by-key', params: { key: 'SIGNATURE_PDF' } })
            .then((response) => {
                alert.removeAll();
                setSignature(response.data);
            })
            .catch((error) => {
                alert.removeAll();
                alert.error(error.message);
            });
        }
    };

    const apiGetLegalNotice = async () => {
        axios({ method: 'get', url: URL_API + '/parameters/get-by-key', params: { key: 'LEGAL_NOTICE' } })
        .then((response) => {
            alert.removeAll();
            setLegalNotice(response.data);
        })
        .catch((error) => {
            alert.removeAll();
            alert.error(error.message);
        });
    };

    const apiGetParticularNotice = async () => {
        axios({ method: 'get', url: URL_API + '/parameters/get-by-key', params: { key: 'PARTICULAR_NOTICE' } })
        .then((response) => {
            alert.removeAll();
            setParticularNotice(response.data);
        })
        .catch((error) => {
            alert.removeAll();
            alert.error(error.message);
        });
    };

    const apiSaveContract = (saveContract) => {
        axios({
            method: 'POST',
            url: `${URL_API}/demande-passage/save-contract`,
            data: saveContract,
            params: {
                userAction: user.user_id,
                planificationId: planificationId ?? 0,
            },
        })
        .then((response) => {
            alert.success('Les accords de passage ont bien été enregistrés !');
        })
        .catch((error) => {
            alert.error(error.message);
        });
    };

    // endregion

    // region Fonctions

    const convertBlobToBase64 = async (blob) => {
        return await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const generatePdf = React.useCallback(async () => {
        try {
            const blob = await pdf(<MyDocument />).toBlob();
            setPdfBlob(blob);
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    }, [fileName]);

    const onSaveContractBtnClick = async () => {
        if (pdfBlob) {
            let base64 = await convertBlobToBase64(pdfBlob);
            let savedContract = { ...saveContract, data: base64.split(',')[1] };
            apiSaveContract(savedContract);
        }
    };

    const getCategory = () => {
        let category = demandePassage.metadata.caracConvoi.category;
        if (category == 3) {
            return '3ème';
        } else if (category == 2) {
            return '2ème';
        } else {
            return '1ère';
        }
    };

    const getChargeMax = () => {
        let chargeMax = 0;

        if (demandePassage.metadata.caracConvoi.configurations) {
            demandePassage.metadata.caracConvoi.configurations.map((config) => {
                if (Number(config.chargeMaxiEssieu) > chargeMax)
                    chargeMax = Number(config.chargeMaxiEssieu);
            });
        }

        return chargeMax > 0 ? parseFloat((Number(chargeMax) / 1000).toString()).toFixed(3) : '-';
    };

    const getDistanceInter = () => {
        let distanceInter = '';

        if (demandePassage.metadata.trajetDescr.descr.some((trajet) => trajet.passType === REQUEST_PASS_TYPE.SUPERIEUR) || demandePassage.metadata.trajetDescr.descrRetour.some((trajet) => trajet.passType === REQUEST_PASS_TYPE.SUPERIEUR)) {
            demandePassage.metadata.caracConvoi.configurations.map((config) => {
                if (config.distanceInterEssieux) {
                    distanceInter += 'Configuration ' + config.index + ' : ' + config.distanceMiniInterEssieu + 'mm\n';
                }
            });
        }

        return distanceInter !== '' ? distanceInter : '-';
    };

    const requestHasPS = () => {
        if (requestPassId > 0) {
            if (demandePassage.metadata.reference.typeDemande === REQUEST_PASS_TYPE.SUPERIEUR) {
                return true;
            }

            let returnState = false;

            demandePassage.metadata.trajetDescr.descr.map((section) => {
                if (section.passType === 'superieur') {
                    returnState = true;
                }
            });

            demandePassage.metadata.trajetDescr.descrRetour.map((section) => {
                if (section.passType === 'superieur') {
                    returnState = true;
                }
            });
            return returnState;
        }

        return false;
    };

    const getElemPatri = () => {
        if (requestPassId > 0) {
            let elemPatris = [];
            demandePassage.metadata.trajetDescr.descr.map((section) => {
                if (demandePassage.metadata.reference.typeDemande === REQUEST_PASS_TYPE.AUTOROUTE && section.passType !== 'superieur') return;
                if (section.elemPatri) {
                    section.elemPatri.map((elemPatri) => {
                        if (elemPatri.status === 'valid') {
                            elemPatris.push(`${elemPatri.id_oa} - ${elemPatri.description ?? '-'} - ${elemPatri.landmark ?? '-'} - ${elemPatri.center ?? '-'} - ${elemPatri.commune ?? '-'}`);
                        }
                    });
                }
            });

            demandePassage.metadata.trajetDescr.descrRetour.map((section) => {
                if (demandePassage.metadata.reference.typeDemande === REQUEST_PASS_TYPE.AUTOROUTE && section.passType !== 'superieur') return;
                if (section.elemPatri) {
                    section.elemPatri.map((elemPatri) => {
                        if (elemPatri.status === 'valid') {
                            elemPatris.push(`${elemPatri.id_oa} - ${elemPatri.description ?? '-'} - ${elemPatri.landmark ?? '-'} - ${elemPatri.center ?? '-'} - ${elemPatri.commune ?? '-'}`);
                        }
                    });
                }
            });

            return elemPatris;
        }
    };

    const getImmat = () => {
        if (!planification) return;
        let immatriculations = planification.metadata.convoyInfos.immatriculations.map((immat) => {
            if (!immat.immatriculation) return null;
            return immat.immatriculation;
        });

        immatriculations = immatriculations.filter(Boolean);
        return immatriculations.length > 0 ? immatriculations.join(', ') : null;
    };

    const getContacts = () => {
        if (!planification) return '-';
        let contacts = planification.metadata.convoyInfos.contact.map((contact) => {
            if (!contact.name && !contact.phone) return null;
            return contact.name + ' ' + contact.phone;
        });

        contacts = contacts.filter(Boolean);
        return contacts.length > 0 ? contacts.join(', ') : null;
    };

    const getCentersInfos = () => {
        if (!planification) return;

        let planifCenters = planification.metadata.centers;
        let requestCenters = demandePassage.metadata.complInfo.refCtr;
        let centers = [];

        if (!planifCenters || !requestCenters) return;

        // Pour full=false, limiter aux centres des tronçons du trajet concerné
        const concernedPath = planification.metadata.concernedPath;
        let relevantCentreIds = null;
        if (concernedPath && String(concernedPath.full) !== "true") {
            const dir = concernedPath.selectedPath;
            const troncons = concernedPath[dir] ?? [];
            relevantCentreIds = new Set(troncons.flatMap(t => (t.centres || []).map(id => Number(id))));
        }

        planifCenters.map((center) => {
            if (relevantCentreIds && !relevantCentreIds.has(Number(center.id))) return;

            let centerName = requestCenters.find((reqCenter) => Number(reqCenter.id) === Number(center.id))?.name;

            if (centerName) {
                centers.push({
                    name: centerName,
                    id: center.id,
                    comment: center.comment !== '' ? center.comment : '-',
                });
            }
        });

        return centers;
    };

    // endregion

    // region Template
    const stylizeChar = (char) => {
        let style = char.getStyle();
        let convertedStyle = [];
        style.map((style) => {
            switch (style) {
                case 'BOLD':
                    convertedStyle = { ...convertedStyle, fontFamily: 'Helvetica-Bold' };
                    break;
                case 'UNDERLINE':
                    convertedStyle = { ...convertedStyle, textDecoration: 'underline' };
                    break;
                case 'ITALIC':
                    convertedStyle = { ...convertedStyle, fontStyle: 'italic' };
                    break;
                default:
                    convertedStyle = { ...convertedStyle, fontFamily: 'Helvetica' };
                    break;
            }
        });
        return convertedStyle;
    };

    const convertComplementDataToHtmlBlock = (blocks) => {
        if (!blocks || blocks.length === 0) return null;

        // Combine the blocks' text into a single structure for better performance
        return blocks.map((block, index) => {
            // Conditional styles
            let textAlign = block.getData().get('text-align') || 'left';
            let justifyContent = 'flex-start';
            if (textAlign === 'center') {
                justifyContent = 'center';
            } else if (textAlign === 'right') {
                justifyContent = 'flex-end';
            }

            let fontSize = '10px';
            if (block.getType() === 'header-one') {
                fontSize = '18px';
            }

            // Combine block text into a single string
            const blockText = block.getText();

            // Render the block with conditional styles
            return (
                <View key={index} style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: justifyContent,  // Dynamic justifyContent
                    lineHeight: 1.5,
                    fontSize: fontSize,              // Dynamic fontSize
                    marginTop: block.getText() === '' || block.getText() === '\n' ? '10px' : '0',
                }}>
                    <Text style={{
                        textAlign: textAlign,        // Dynamic textAlign
                        flex: 1,
                        fontFamily: block.getType() === 'header-one' || block.getType() === 'header-four' ? 'Helvetica-Bold' : 'Helvetica',
                        fontWeight: block.getType() === 'header-one' || block.getType() === 'header-four' ? 'bold' : 'normal',
                        textDecoration: block.getType() === 'header-one' ? 'underline' : 'none',
                    }}>
                        {blockText}
                    </Text>
                </View>
            );
        });
    };


    const convertHtmlToDraftJs = (html) => {
        let { contentBlocks, entityMap } = htmlToDraft(html);
        return { contentBlocks, entityMap };
    };

    const formatLegalNotice = React.useMemo(() => {
        if (legalNotice) {
            let render = convertHtmlToDraftJs(legalNotice);
            // Instead of rendering each block individually, combine and render in a single pass
            return convertComplementDataToHtmlBlock(render.contentBlocks);
        }
        return null;
    }, [legalNotice]);

    const formatParticularNotice = React.useMemo(() => {
        if (particularNotice) {
            let render = convertHtmlToDraftJs(particularNotice);
            // Same approach for particular notice
            return convertComplementDataToHtmlBlock(render.contentBlocks);
        }
        return null;
    }, [particularNotice]);

    const Br = () => '\n';

    const departmentNumbers = demandePassage?.metadata.reference.departments.map(department => {
        const match = department.match(/\((\d+)\)/);
        return match ? match[1] : null;
    }).filter(Boolean);

    const ContractTemplate = () => {
        return (
            <Page size='A4' style={styles.page} dpi={74}>
                {demandePassage &&
                    <>
                        <Image
                            src={logoSanef}
                            style={{
                                maxHeight: '80px',
                                maxWidth: '120px',
                                marginLeft: '50px',
                                width: '120px',
                            }}
                            fixed={true}
                        />
                        <View style={styles.sectionHead}>
                            <View style={{
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                margin: '0 50px',
                            }}>
                                <View style={{
                                    fontSize: '10px',
                                }}>
                                    <Text>Pétitionnaire :
                                        <Br />{demandePassage.metadata.complementIdentite.transporteur}
                                        <Br />{demandePassage.metadata.complementIdentite.adresse}
                                        <Br />{demandePassage.metadata.complementIdentite.codePostal} {demandePassage.metadata.complementIdentite.commune}
                                        <Br />{demandePassage.metadata.complementIdentite.pays}
                                    </Text>
                                </View>

                            </View>
                            <View style={{
                                marginTop: 15,
                                marginLeft: '50px',
                                fontSize: '10px',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                            }}>
                                <View>
                                    <Text>
                                        Numéro de dossier : {demandePassage.id}
                                    </Text>
                                    <Text>
                                        Numéro DDT : {demandePassage.metadata.reference.numDossier}
                                    </Text>
                                    <Text>
                                        Département(s) concerné(s) : {departmentNumbers.join('- ')}
                                    </Text>
                                </View>
                                <View>
                                    <Text
                                        render={() => ('Ormes, le ' + (currentDate.format('DD/MM/YYYY')))}></Text>
                                </View>
                            </View>

                        </View>
                        <View style={{
                            justifyContent: 'center',
                            margin: '15px 50px',
                            fontSize: '21px',
                            fontWeight: 900,
                        }}>

                            <Text
                                style={{ textAlign: 'center' }}>{'ACCORD DE PASSAGE N° 1'} <Br />DU DOSSIER N° {requestPassId} POUR LA DEMANDE N° {numDemand}</Text>
                        </View>

                        <View style={{ ...styles.sectionBody, height: 'auto', margin: 0 }}>
                            <View style={{ height: 'auto' }}>
                                <View style={{ marginLeft: '50px', marginRight: '50px' }}>
                                    <View style={{ fontSize: '10px' }}>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Arrêté N°:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>
                                                {demandePassage.metadata.reference.numDossier} en date
                                                du {demandePassage.metadata.complInfo.dateArrete ? moment(demandePassage.metadata.complInfo.dateArrete).format('LL') : '-'}
                                            </Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Nombre de
                                                convois:</Text>
                                            <Text style={{ textAlign: 'left', padding: 3, width: '70%' }}
                                                  render={() => `${demandePassage.metadata.datePassage.nbVehiculeAller + demandePassage.metadata.datePassage.nbVehiculeRetour} convoi(s) (${demandePassage.metadata.datePassage.nbVehiculeAller} aller(s) ${(demandePassage.metadata.datePassage.nbPassageRetour !== 0 || demandePassage.metadata.datePassage.nbVehiculeRetour !== 0) ? ' et ' + demandePassage.metadata.datePassage.nbVehiculeRetour + ' retour(s)' : ''})`}></Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>DESC:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>
                                                {demandePassage.metadata.complInfo.desc1Number ? `\n${demandePassage.metadata.complInfo.desc1Number} en date du ${moment(demandePassage.metadata.complInfo.desc1Date).format('LL')}` : '-'}
                                                {demandePassage.metadata.complInfo.desc2Number ? `\n${demandePassage.metadata.complInfo.desc2Number} en date du ${moment(demandePassage.metadata.complInfo.desc2Date).format('LL')}` : ''}
                                            </Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Catégorie:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{getCategory()}</Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Date de
                                                passage:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{planification ? moment(planification.metadata.startDate).format('LL') : '-'}</Text>
                                        </View>

                                        {/* TODO : demandePassage.metadata.complInfo.startDate + demandePassage.metadata.complInfo.endDate */}
                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Horaires de
                                                passage:</Text>
                                            <Text style={{ textAlign: 'left', padding: 3, width: '70%' }}
                                                  render={() => (`${planification ? `${moment(planification.metadata.startDate).format('HH:mm')} - ${moment(planification.metadata.endDate).format('HH:mm')}` : 'Non défini'}`)}></Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Liaison:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{demandePassage.metadata.trajetDescr.depart} - {demandePassage.metadata.trajetDescr.arrivee}</Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Itinéraire sur
                                                autoroute:</Text>
                                            <Text style={{ textAlign: 'left', padding: 3, width: '70%' }}>
                                                {(() => {
                                                    const cp = planification?.metadata?.concernedPath;
                                                    const isFull = cp?.full == true;
                                                    const selectedPath = cp?.selectedPath;
                                                    const allerSections = cp?.aller ?? [];
                                                    if (isFull) {
                                                        // Trajet complet : tous les tronçons aller (sauf si direction retour)
                                                        return demandePassage.metadata.trajetDescr.descr
                                                            .filter(s => s.passType !== 'superieur' && s.nomSection && selectedPath !== 'retour')
                                                            .map(s => s.nomSection)
                                                            .join(' / ');
                                                    }
                                                    // Sélection partielle : croiser avec concernedPath.aller[]
                                                    return demandePassage.metadata.trajetDescr.descr
                                                        .filter(s => s.passType !== 'superieur' && s.nomSection && allerSections.some(a => a.numeroSection === s.numeroSection))
                                                        .map(s => s.nomSection)
                                                        .join(' / ');
                                                })()}
                                                {' - '}
                                                {(() => {
                                                    const cp = planification?.metadata?.concernedPath;
                                                    const isFull = cp?.full == true;
                                                    const selectedPath = cp?.selectedPath;
                                                    const retourSections = cp?.retour ?? [];
                                                    if (isFull) {
                                                        // Trajet complet : tous les tronçons retour (seulement si direction retour)
                                                        return demandePassage.metadata.trajetDescr.descrRetour
                                                            .filter(s => s.passType !== 'superieur' && s.nomSection && selectedPath === 'retour')
                                                            .map(s => s.nomSection)
                                                            .join(' / ');
                                                    }
                                                    // Sélection partielle : croiser avec concernedPath.retour[]
                                                    return demandePassage.metadata.trajetDescr.descrRetour
                                                        .filter(s => s.passType !== 'superieur' && s.nomSection && retourSections.some(r => r.numeroSection === s.numeroSection))
                                                        .map(s => s.nomSection)
                                                        .join(' / ');
                                                })()}
                                            </Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Nature du
                                                chargement:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{demandePassage.metadata.caracConvoi.natureCharge ?? '-'}</Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', padding: 3 }}>Caractéristiques
                                                maximales:</Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Longueur:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{(Number(demandePassage.metadata.caracConvoi.longueurCharge) / 1000).toFixed(2) ?? '-'} m</Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Largeur:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{(Number(demandePassage.metadata.caracConvoi.largeurCharge) / 1000).toFixed(2) ?? '-'} m</Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Hauteur:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{(Number(demandePassage.metadata.caracConvoi.hauteurCharge) / 1000).toFixed(2) ?? '-'} m</Text>
                                        </View>

                                        <View
                                            style={{ ...styles.caracRow, borderRight: 1, borderLeft: 1, borderTop: 1 }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Poids total en
                                                charge maxi:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{parseFloat((Number(demandePassage.metadata.caracConvoi.masseTotaleCharge) / 1000).toString()).toFixed(3)} T</Text>
                                        </View>

                                        <View style={{
                                            ...styles.caracRow,
                                            borderRight: 1,
                                            borderLeft: 1,
                                            borderTop: 1,
                                        }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Charge maxi par
                                                essieu:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{getChargeMax()} T</Text>
                                        </View>

                                        {demandePassage.metadata.trajetDescr.descr.some((trajet) => trajet.passType === REQUEST_PASS_TYPE.SUPERIEUR) || demandePassage.metadata.trajetDescr.descrRetour.some((trajet) => trajet.passType === REQUEST_PASS_TYPE.SUPERIEUR) &&
                                            <View style={{
                                                ...styles.caracRow,
                                                borderRight: 1,
                                                borderLeft: 1,
                                                borderTop: 1,
                                            }}>
                                                <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Distance
                                                    inter-essieux:</Text>
                                                <Text style={{
                                                    textAlign: 'left',
                                                    padding: 3,
                                                    width: '70%',
                                                }}>{getDistanceInter()}</Text>
                                            </View>
                                        }

                                        {demandePassage.metadata.caracConvoi.configurations.length > 0 &&
                                            <>
                                                <View style={{
                                                    ...styles.caracRow,
                                                    borderRight: 1,
                                                    borderLeft: 1,
                                                    borderTop: 1,
                                                }}>
                                                    <Text style={{ width: '30%', padding: 3 }}>Configurations :</Text>
                                                    <Text style={{ textAlign: 'left', padding: 3, width: '35%' }}>Charge
                                                        maxi par essieu</Text>
                                                    <Text style={{ textAlign: 'left', padding: 3, width: '35%' }}>Distance
                                                        mini entre essieux</Text>
                                                </View>

                                                {demandePassage.metadata.caracConvoi.configurations.map((config, index, array) => (
                                                    <View style={{
                                                        ...styles.caracRow,
                                                        borderRight: 1,
                                                        borderLeft: 1,
                                                        borderTop: 1,
                                                        borderBottom: (!(requestHasPS() && getElemPatri().length > 0) && index === array.length - 1) ? 1 : 0,
                                                    }}>
                                                        <Text style={{
                                                            width: '30%',
                                                            borderRight: 1,
                                                            padding: 3,
                                                        }}>N° {config.index}</Text>
                                                        <Text style={{
                                                            textAlign: 'left',
                                                            padding: 3,
                                                            width: '35%',
                                                        }}>{parseFloat((Number(config.chargeMaxiEssieu) / 1000).toString()).toFixed(3)} T</Text>
                                                        <Text style={{
                                                            textAlign: 'left',
                                                            padding: 3,
                                                            width: '35%',
                                                        }}>{parseFloat((Number(config.distanceMiniInterEssieu) / 1000).toString()).toFixed(2)} m</Text>
                                                    </View>
                                                ))}
                                            </>
                                        }

                                        {(requestHasPS() && getElemPatri().length > 0) &&
                                            <>
                                                <View style={{
                                                    ...styles.caracRow,
                                                    borderRight: 1,
                                                    borderLeft: 1,
                                                    borderTop: 1,
                                                    borderBottom: 1,
                                                }}>
                                                    <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Elements
                                                        Patrimoine traversés en PS</Text>
                                                    <Text style={{ textAlign: 'left', padding: 3, width: '70%' }}>
                                                        {getElemPatri().map((elemPatri, index) => (
                                                            <React.Fragment key={index}>
                                                                {elemPatri}
                                                                <Br />
                                                            </React.Fragment>
                                                        ))}
                                                    </Text>
                                                </View>
                                            </>
                                        }

                                        <View style={{
                                            ...styles.caracRow,
                                            borderRight: 1,
                                            borderLeft: 1,
                                            borderTop: 1,
                                        }}>
                                            <Text style={{
                                                width: '30%',
                                                borderRight: 1,
                                                padding: 3,
                                            }}>Immatriculations:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{getImmat() ?? '-'}</Text>
                                        </View>

                                        <View style={{
                                            ...styles.caracRow,
                                            borderRight: 1,
                                            borderLeft: 1,
                                            borderTop: 1,
                                            borderBottom: 1,
                                        }}>
                                            <Text style={{ width: '30%', borderRight: 1, padding: 3 }}>Contacts:</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{getContacts() ?? '-'}</Text>
                                        </View>

                                    </View>
                                </View>

                                <View style={{ display: 'flex', margin: '5px auto', padding: '0 50px' }}>
                                    <Text style={{ fontSize: '11px', fontWeight: 800, textAlign: 'center' }}>
                                        {('L\'emprunt des autoroutes concédées est soumis aux prescriptions générales et particulières indiquées à la suite de ce document.').toUpperCase()}
                                    </Text>
                                    <Text style={{ fontWeight: 800, textAlign: 'center' }}>Rappel : le péage est à
                                        acquitter par chaque véhicule à chaque passage</Text>
                                </View>
                            </View>
                        </View>

                        <View style={{ position: 'relative', marginBottom: '50px' }}>
                            <View style={{ marginLeft: '50px', marginRight: '50px', fontSize: '10px' }}>
                                <View style={{ marginBottom: '10px' }}>
                                    <Text style={{ fontSize: '12px', textDecoration: 'underline' }}>
                                        Mesures d'exploitation :
                                    </Text>
                                </View>

                                <View>
                                    <Text style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: 10 }}>
                                        {demandePassage.metadata.caracConvoi.accompagnement ? 'Accompagnement Sanef préconisé sur l\'ensemble de l\'itinéraire décrit ci-dessus. ' : '-'}
                                    </Text>
                                </View>

                                {getCentersInfos() && getCentersInfos().map((center, index) => {
                                    if (!center) return null;
                                    if (!center.comment) return null;
                                    let nbCenter = getCentersInfos().filter((center) => center && center.comment).length;
                                    return (
                                        <View wrap={false} style={{
                                            ...styles.caracRow,
                                            borderRight: 1,
                                            borderLeft: 1,
                                            borderTop: 1,
                                            borderBottom: index === nbCenter - 1 ? 1 : 0,
                                            marginBottom: index === nbCenter - 1 ? 10 : 0,
                                        }}>
                                            <Text style={{
                                                width: '30%',
                                                borderRight: 1,
                                                padding: 3,
                                            }}>{center.name}</Text>
                                            <Text style={{
                                                textAlign: 'left',
                                                padding: 3,
                                                width: '70%',
                                            }}>{center.comment}</Text>
                                        </View>
                                    );
                                })}

                                <View>
                                    <Text>A l’entrée sur notre réseau et en cas de difficultés contacter le PCE concerné
                                        par votre trajet:</Text>
                                    <View style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        fontWeight: 'bold',
                                        marginLeft: 15,
                                    }}>
                                        <Text>- PCE NORD : 03.44.63.72.70</Text>
                                        <Text>- PCE OUEST : 02.35.18.31.95</Text>
                                        <Text>- PCE EST : 03.87.39.41.88 </Text>
                                    </View>
                                </View>

                            </View>

                            <View style={{ marginLeft: '50px', marginRight: '50px', marginTop: 10, fontSize: '10px' }}>
                                <View style={{ flexDirection: 'column', marginBottom: '10px' }}>
                                    <Text style={{ fontSize: '11px', textDecoration: 'underline' }}>Prescriptions
                                        Service Ouvrage d'Art :</Text>
                                    <Text
                                        style={{
                                            marginLeft: '10px',
                                            marginTop: '10px',
                                        }}>{demandePassage.metadata.complInfo.servOaInfo ?? '-'}</Text>
                                </View>

                            </View>

                            <View>
                                <View style={{ marginLeft: '50px', marginRight: '50px' }}>
                                    <Text style={{ fontSize: '10px', marginTop: 5 }}>
                                        Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations
                                        distinguées.
                                    </Text>
                                    <>
                                        {signature && <Image src={signature} style={{
                                            maxHeight: '50px',
                                            maxWidth: '150px',
                                            marginTop: 10,
                                            marginLeft: 'auto',
                                            width: '150px',
                                        }} />}
                                    </>
                                </View>
                            </View>
                        </View>

                        <View style={{
                            display: 'flex',
                            flexDirection: 'column',
                            fontSize: '10px',
                            margin: '20px 40px',
                            justifyContent: 'center',
                        }} break={true}>
                            {legalNotice && formatLegalNotice}
                        </View>

                        <View style={{
                            display: 'flex',
                            flexDirection: 'column',
                            fontSize: '10px',
                            margin: '20px 40px',
                            justifyContent: 'center',
                        }} break={true}>
                            {particularNotice && formatParticularNotice}
                        </View>

                    </>}
                <View fixed={true}
                      style={{ position: 'absolute', fontSize: 5, bottom: 15, left: 0, right: 0, textAlign: 'center' }}>
                    <Text>
                        Site de l'Ecopôle - Route de Thillois - Ormes
                        <Br />
                        CS 30011 51886 REIMS Cedex
                        <Br />
                        S.A. au capital de 53 090 461,67 euros - RCS Nanterre B632 050 019
                    </Text>
                </View>
            </Page>
        );
    };

    const MyDocument = React.memo(() => {
        return <PdfGenerator title={`Accords de passage demande n° [${requestPassId}]`}
                             template={ContractTemplate()} />;
    });

    useEffect(() => {
        if (!pdfBlob && fileName) {
            generatePdf();
        }
    }, [fileName]);


    //#endregion

    return (
        <div style={{ height: '100%', width: '100%' }}>
            <TopBar title={'Accords de passage demande n° [' + requestPassId + ']'} className='topbar-contenu'
                    color={props.params.color}
                    buttons={
                        <div className='topbar-buttons'>
                            <MyButton label='Retour' icon={faArrowLeft} onClick={() => {
                                navigate('/planification');
                            }} />

                            {fileName && pdfBlob && (
                                <PDFDownloadLink
                                    document={<MyDocument />}
                                    fileName={fileName + '.pdf'}
                                    blob={pdfBlob}
                                >
                                    {({ loading }) => (
                                        loading ? (
                                            <MyButton label='Génération...' icon={faFilePdf} disabled={true} />
                                        ) : (
                                            <>
                                                <MyButton label='Sauvegarder et notifier le client' icon={faSave}
                                                          onClick={onSaveContractBtnClick} />
                                                <MyButton label='Télécharger' icon={faFilePdf} />
                                            </>
                                        )
                                    )}
                                </PDFDownloadLink>
                            )}
                        </div>
                    }
            />
            {pdfBlob ? (
                <PDFViewer style={{ height: '94%', width: '100%' }} showToolbar={false}>
                    <MyDocument />
                </PDFViewer>
            ) : (
                <p>Chargement...</p>
            )}
        </div>
    );
};

export default ContractViewer;